import type { Furniture, PlacedModule, SectionalModuleId, Vec2 } from "../types";
import { polygonsOverlap } from "../geom";
import {
  getModule,
  placedModuleOutline,
  placedModulePorts,
  type ModulePort,
} from "./modules";
import { centerModules } from "./resolve";

/**
 * Pure editing operations for modular sectionals. Modules connect through
 * *ports* (edge connectors with outward normals); a port is "free" when no
 * other module's port sits on it facing the opposite way. Dragging a module
 * near a free port snaps it so the two ports coincide and the module is
 * rotated to face the right way.
 */

const PORT_COINCIDE_IN = 0.75;
/** How close (inches) a dragged module must get to a candidate to snap. */
export const SNAP_RADIUS_IN = 18;

function portsCoincide(a: ModulePort, b: ModulePort): boolean {
  const d = Math.hypot(a.x - b.x, a.y - b.y);
  if (d > PORT_COINCIDE_IN) return false;
  const dot = a.nx * b.nx + a.ny * b.ny;
  return dot < -0.9;
}

/** Ports on every module except `excludeId` that aren't connected to anything. */
export function freePorts(modules: PlacedModule[], excludeId?: string): ModulePort[] {
  const others = modules.filter((m) => m.id !== excludeId);
  const all = others.flatMap((m) => placedModulePorts(m));
  return all.filter((p) => !all.some((q) => q !== p && portsCoincide(p, q)));
}

export interface SnapCandidate {
  x: number;
  y: number;
  rotation: number;
  /** Distance from the desired position, inches. */
  dist: number;
  /** The port on the stationary module this candidate attaches to. */
  port: ModulePort;
}

/**
 * All valid placements for module `moduleId` that attach one of its ports to
 * a free port of the other modules, sorted by distance from `desired`.
 * Candidates that overlap another module are dropped.
 */
export function snapCandidates(
  modules: PlacedModule[],
  movingId: string,
  moduleId: SectionalModuleId,
  desired: Vec2
): SnapCandidate[] {
  const def = getModule(moduleId);
  const free = freePorts(modules, movingId);
  const others = modules.filter((m) => m.id !== movingId);
  const otherOutlines = others.map(placedModuleOutline);
  const out: SnapCandidate[] = [];
  for (const P of free) {
    for (const Q of def.ports) {
      // Rotation that turns Q's normal into -P's normal.
      const targetAngle = Math.atan2(-P.ny, -P.nx);
      const qAngle = Math.atan2(Q.ny, Q.nx);
      let rot = ((targetAngle - qAngle) * 180) / Math.PI;
      rot = Math.round(((rot % 360) + 360) % 360);
      // Position so the rotated Q lands on P.
      const rad = (rot * Math.PI) / 180;
      const c = Math.cos(rad);
      const s = Math.sin(rad);
      const qx = Q.x * c - Q.y * s;
      const qy = Q.x * s + Q.y * c;
      const x = P.x - qx;
      const y = P.y - qy;
      const placed: PlacedModule = { id: movingId, moduleId, x, y, rotation: rot };
      const outline = placedModuleOutline(placed);
      // Shrink slightly so shared edges don't register as overlap.
      const shrunk = shrinkOutline(outline, 0.25);
      if (otherOutlines.some((o) => polygonsOverlap(shrunk, o))) continue;
      out.push({ x, y, rotation: rot, dist: Math.hypot(x - desired.x, y - desired.y), port: P });
    }
  }
  out.sort((a, b) => a.dist - b.dist);
  return out;
}

function shrinkOutline(outline: Vec2[], by: number): Vec2[] {
  let cx = 0;
  let cy = 0;
  for (const p of outline) {
    cx += p.x;
    cy += p.y;
  }
  cx /= outline.length;
  cy /= outline.length;
  return outline.map((p) => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    const d = Math.hypot(dx, dy) || 1;
    const k = Math.max(0, d - by) / d;
    return { x: cx + dx * k, y: cy + dy * k };
  });
}

let moduleCounter = 0;
export function newModuleId(): string {
  moduleCounter += 1;
  return `m${Date.now().toString(36)}${moduleCounter}`;
}

/** Add a module at the free port closest to `near` (or the first free port). */
export function addModule(
  modules: PlacedModule[],
  moduleId: SectionalModuleId,
  near?: Vec2
): PlacedModule[] {
  const id = newModuleId();
  if (modules.length === 0) {
    return [{ id, moduleId, x: 0, y: 0, rotation: 0 }];
  }
  const desired = near ?? { x: 0, y: 0 };
  const candidates = snapCandidates(modules, id, moduleId, desired);
  if (candidates.length === 0) {
    // Nowhere to attach — drop it beside the arrangement rather than fail.
    const maxX = Math.max(...modules.flatMap((m) => placedModuleOutline(m).map((p) => p.x)));
    const def = getModule(moduleId);
    return [...modules, { id, moduleId, x: maxX + def.w / 2 + 6, y: 0, rotation: 0 }];
  }
  const c = candidates[0];
  return [...modules, { id, moduleId, x: c.x, y: c.y, rotation: c.rotation }];
}

export function removeModule(modules: PlacedModule[], id: string): PlacedModule[] {
  if (modules.length <= 1) return modules;
  return modules.filter((m) => m.id !== id);
}

/** Move a module toward `desired`, snapping to the nearest valid port. */
export function moveModule(
  modules: PlacedModule[],
  id: string,
  desired: Vec2,
  opts: { forceSnap?: boolean } = {}
): { modules: PlacedModule[]; snapped: boolean } {
  const m = modules.find((x) => x.id === id);
  if (!m) return { modules, snapped: false };
  if (modules.length === 1) {
    return { modules: modules.map((x) => (x.id === id ? { ...x, x: desired.x, y: desired.y } : x)), snapped: true };
  }
  const candidates = snapCandidates(modules, id, m.moduleId, desired);
  const best = candidates[0];
  if (best && (opts.forceSnap || best.dist <= SNAP_RADIUS_IN)) {
    return {
      modules: modules.map((x) => (x.id === id ? { ...x, x: best.x, y: best.y, rotation: best.rotation } : x)),
      snapped: true,
    };
  }
  if (opts.forceSnap) return { modules, snapped: false };
  return {
    modules: modules.map((x) => (x.id === id ? { ...x, x: desired.x, y: desired.y } : x)),
    snapped: false,
  };
}

/** Rotate a module 90° and re-attach it to the nearest valid port,
 *  preferring an attachment that keeps the requested rotation (symmetric
 *  modules like ottomans can attach at the same spot in several rotations). */
export function rotateModule(modules: PlacedModule[], id: string): PlacedModule[] {
  const m = modules.find((x) => x.id === id);
  if (!m) return modules;
  const wanted = (m.rotation + 90) % 360;
  if (modules.length === 1) return modules.map((x) => (x.id === id ? { ...x, rotation: wanted } : x));
  const candidates = snapCandidates(modules, id, m.moduleId, { x: m.x, y: m.y });
  if (candidates.length === 0) return modules;
  const exact = candidates.filter((c) => c.rotation === wanted);
  const best = (exact.length ? exact : candidates)[0];
  return modules.map((x) => (x.id === id ? { ...x, x: best.x, y: best.y, rotation: best.rotation } : x));
}

/**
 * Commit an edited module set back onto a `Furniture`: recenters the union
 * on the origin, updates `width`/`depth`, and shifts `x`/`y` (in room
 * space, honoring rotation) so nothing visibly jumps.
 */
export function applyModules(f: Furniture, modules: PlacedModule[]): Partial<Furniture> {
  const centered = centerModules(modules);
  const rad = (f.rotation * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  // Modules moved by (dx, dy) in local space; the piece center must move by
  // the opposite amount (rotated into room space) to keep them in place.
  const shiftX = -centered.dx;
  const shiftY = -centered.dy;
  return {
    model: { kind: "modular", modules: centered.modules },
    width: centered.width,
    depth: centered.depth,
    x: f.x + shiftX * c - shiftY * s,
    y: f.y + shiftX * s + shiftY * c,
  };
}
